// RU-PNLTAB: dedicated P&L page — realized P&L by segment → type → symbol → trades for a period.
import { useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import PnLBreakdownTree from "@/components/PnLBreakdownTree";

const IST_MS = 330 * 60 * 1000;

// YYYY-MM-DD of the IST calendar day `daysAgo` before `now`.
export function istDay(now = Date.now(), daysAgo = 0) {
  return new Date(now + IST_MS - daysAgo * 86400000).toISOString().slice(0, 10);
}

export function presetRange(preset, now = Date.now()) {
  const today = istDay(now);
  const d = new Date(`${today}T00:00:00Z`);
  if (preset === "week") {
    const back = (d.getUTCDay() + 6) % 7;          // Monday start
    return { from: istDay(now, back), to: today };
  }
  if (preset === "month") return { from: `${today.slice(0, 8)}01`, to: today };
  if (preset === "30d") return { from: istDay(now, 29), to: today };
  return { from: today, to: today };
}

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "30d", label: "Last 30 days" },
  { key: "custom", label: "Custom" },
];

export default function PnL() {
  const [mode, setMode] = useState("paper");
  const [preset, setPreset] = useState("today");
  const [custom, setCustom] = useState(() => presetRange("today"));

  const range = useMemo(() => (preset === "custom" ? custom : presetRange(preset)), [preset, custom]);
  const validCustom = range.from && range.to && range.from <= range.to;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        <PageHeader
          overline="Analytics"
          title="P&L"
          description="Realized profit & loss of trades placed through AlgoForge, by segment, type and symbol"
        />

        <div className="flex flex-wrap items-center gap-2">
          {["paper", "live", "combined"].map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`mode-${m}`}
              onClick={() => setMode(m)}
              className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors ${
                mode === m ? "bg-indigo-600 border-indigo-500 text-white"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"}`}
            >
              {m.charAt(0).toUpperCase() + m.slice(1)}
            </button>
          ))}
          <span className="w-px h-5 bg-zinc-800 mx-1" />
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              data-testid={`preset-${p.key}`}
              onClick={() => { if (p.key === "custom") setCustom(range); setPreset(p.key); }}
              className={`px-2.5 py-1 text-xs rounded-sm border transition-colors ${
                preset === p.key ? "border-indigo-500 text-indigo-300" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}
            >
              {p.label}
            </button>
          ))}
          {preset === "custom" && (
            <span className="flex items-center gap-1 text-xs text-zinc-400">
              <input type="date" data-testid="from" value={custom.from} max={custom.to}
                     onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
                     className="bg-zinc-900 border border-zinc-700 rounded-sm px-1.5 py-0.5" />
              <span>to</span>
              <input type="date" data-testid="to" value={custom.to} min={custom.from}
                     onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
                     className="bg-zinc-900 border border-zinc-700 rounded-sm px-1.5 py-0.5" />
            </span>
          )}
        </div>
        <div className="text-[11px] text-zinc-600" data-testid="range-label">
          {range.from === range.to ? range.from : `${range.from} → ${range.to}`} (IST)
        </div>

        {validCustom ? (
          <PnLBreakdownTree mode={mode} from={range.from} to={range.to} />
        ) : (
          <div className="text-xs text-zinc-600">Pick a valid date range</div>
        )}
      </div>
    </AppShell>
  );
}
